import axios from "axios"
import { Currency, Genre, StageType } from "./common"

export interface CompanyInvoiceGenerate {
  invoicingPeriod: {
    startDate: string
    endDate: string
  },
  companyId: string
}

export interface CompanyRecord {
  id: string
  name: string
  membersCount: number,
  filled: boolean,
  ngo: boolean,
  foldingEnabled: boolean,
  paymentThreshold?: number,
  minHourlyRate?: number,
  maxHourlyRate?: number,
}

export interface CompanySearchRecord {
  id: string
  name: string
}

export interface CompanyBillingInformation {
  financialInfo?: CompanyFinancialInfo,
  contactPerson?: ContactPerson,
  paymentMethods: CompanyPaymentMethodVariant[]
}

export interface CompanyFinancialInfo {
  name: string,
  vatNum: string,
  address: Address,
  currency: Currency,
  paymentThreshold?: number
}

export interface Address {
  country: string,
  city: string,
  street: string,
  streetNum: string,
  postalCode: string,
  state?: string
}

export interface ContactPerson {
  name: string,
  surname: string,
  mail: string,
  phone: string
}

export type PaypalMethod = {
  paymentMethodId: string | null,
  paypalUser: string,
  isDefault: boolean,
}
export type BankMethod = {
  paymentMethodId: string | null,
  bankName: string,
  accountNumber: string,
  bic: string,
  routingNumber: string | null,
  isDefault: boolean,
}
export type IbanMethod = {
  paymentMethodId: string | null,
  bankName: string,
  iban: string,
  accountHolder: string,
  isDefault: boolean,
}

export type CompanyPaymentMethodVariant = {
  paypalPayment: PaypalMethod,
} | {
  bankPayment: BankMethod,
} | {
  ibanPayment: IbanMethod,
}

export interface HourlyRateLimits {
  min: number,
  max: number,
  currency: Currency
}

export async function generateCompanyInvoice (invoiceData: CompanyInvoiceGenerate) {
  await axios.put<void>(`/api/admin/companies/${invoiceData.companyId}/generateInvoice`, invoiceData)
}

export async function getCompanyFinancialData (companyId: string) {
  const response = await axios.get<CompanyBillingInformation>(`/api/admin/companies/${companyId}/billing`)
  return response.data
}

export async function getCompanyHourlyRateLimits (companyId: string) {
  const response = await axios.get<HourlyRateLimits>(`/api/admin/companies/${companyId}/hourlyRateLimits`)
  return response.data
}

export async function getSearchCompany (search: string) {
  const response = await axios.get<CompanySearchRecord[]>(`/api/admin/companies/search`, {
    params: { name: search }
  })
  return response.data
}

export type CompanyMemberRole = "ORGANIZATION_ADMIN" | "ORGANIZATION_MEMBER"

export async function updateCompanyMember (memberId: string, newRole: CompanyMemberRole) {
  await axios.put<void>(`/api/admin/companies/members/${memberId}`, { newRole: newRole })
}

export interface CompanyPortalProperties {
  companyId: string | null,
  showDiscoverMe: boolean,
  allowedLanguageList: string[],
  defaultLanguageCombinationTranslateAndCreateFrom: string,
  defaultLanguageCombinationTranslateAndCreateTo: string,
  defaultLanguageCombinationFileFrom: string,
  defaultLanguageCombinationFileTo: string,
  genre: Genre,
  taxonomy: string
}

export async function getCompanyPortalProperties (companyId: string) {
  try {
    const response = await axios.get<CompanyPortalProperties>(`/portal/api/companies/${companyId}/portal-properties`)
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null
    }
    throw error
  }
}

export async function setCompanyPortalProperties (companyPortalProperties: CompanyPortalProperties) {
  await axios.put<void>(`/portal/api/companies/${companyPortalProperties.companyId}/portal-properties`, companyPortalProperties )
}

export type PricingType = 'FIXED' | 'DYNAMIC' | 'FIXED_ASSET_BASED'

export type TabletType = "KM" | "INSOURCING" | "OUTSOURCING"

export type TabletStatus = "ACTIVE" | "INACTIVE" | "DISABLED"

export interface TranslateFileTabletStage {
  id: string
  stage: StageType
}

export interface Tablet{
  id: string
  name?: string
  title: string
  description?: string
  path: string
  type: TabletType
  status: TabletStatus
  priorityGroup: number
  tabletOrder: number
  quoting: boolean
  multiLang: boolean
  stages?: TranslateFileTabletStage[]
  pricingType: PricingType
  generateTcert?: boolean
  pricingRuleId: string
}

export const setupCommonTablets = (companyId: string) => axios.post(`/portal/api/tablets/common`, null, { params: { companyId } })

export const updateTablets = (companyId: string, tablets: Tablet[]) => axios.put(`/portal/api/tablets`, tablets, { params: { companyId } })

export async function setCompanyPortalLogo (companyId: string, svgFile: any) {
  const form = new FormData()
  form.append("file", svgFile)
  await axios.put<void>(`/portal/api/companies/${companyId}/portal-logo`, form)
}

export async function getCopanyPortalLogoPath (companyId: string) {  
  const response = await axios.get<string>(`/portal/api/companies/${companyId}/portal-logo-path`)
  return response.data
}

export async function searchTaxonomy (search: string, sourceRef: React.MutableRefObject<AbortController | undefined> | undefined) {  
  const controller = new AbortController()
  const { signal } = controller
  if (sourceRef) {
    sourceRef.current = controller
  }

  const response = await axios.get<any>('/api/workflow/my_exfluency/taxonomy', { 
    signal, 
    params: { query: search } 
  })
  return response.data
}

// Email Domains API
export async function getCompanyEmailDomains(companyId: string) {
  const response = await axios.get<string[]>(`/api/admin/companies/${companyId}/email-domains`)
  return response.data
}

export async function addCompanyEmailDomain(companyId: string, emailDomain: string) {
  const encodedDomain = btoa(emailDomain)
  await axios.post(`/api/admin/companies/${companyId}/email-domains/${encodedDomain}`)
}

export async function deleteCompanyEmailDomain(companyId: string, emailDomain: string) {
  const encodedDomain = btoa(emailDomain)
  await axios.delete(`/api/admin/companies/${companyId}/email-domains/${encodedDomain}`)
}

// Gated Community API
export async function getCompanyGatedCommunity(companyId: string) {
  try {
    const response = await axios.get<string>(`/api/admin/companies/${companyId}/gated-community`)
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null
    }
    throw error
  }
}

export async function setCompanyGatedCommunity(companyId: string, gatedCommunityId: string) {
  await axios.post(`/api/admin/companies/${companyId}/gated-community/${gatedCommunityId}`)
}

export async function deleteCompanyGatedCommunity(companyId: string) {
  await axios.delete(`/api/admin/companies/${companyId}/gated-community`)
}

// Company Deadlines API
export interface DeadlineEntry {
  wordCountFrom: number
  wordCountTo: number
  deadlineHours: number
  responseTimeHours: number
}

export interface TatEntry {
  wordCountFrom: number
  wordCountTo: number
  totalTat: number
  redaction: number
  enhancement: number
  trustMining: number
}

export interface CompanyDeadlinesResponse {
  redaction: DeadlineEntry[]
  enhancement: DeadlineEntry[]
  trustMining: DeadlineEntry[]
  tatBusinessDays: TatEntry[]
}

export async function getCompanyDeadlines(companyId: string) {
  const response = await axios.get<CompanyDeadlinesResponse>(
    `/api/workflow/companies/${companyId}/deadlines`
  )
  return response.data
}